<template>
    <div class="catalog-sort">
        <button
            ref="button"
            v-click-outside="() => (listOpen = false)"
            class="catalog-sort__btn btn btn--small"
            :class="{ 'btn--bg': isSortActive }"
            @click="toggleList"
            @keydown.enter="toggleList"
            @keydown.space="toggleList"
        >
            <span
                v-if="isSortActive"
                class="catalog-sort__icon sort-dot for-mobile"
            >
                <SortDotIcon height="20" />
            </span>
            <span class="catalog-sort__label">
                <slot>{{ selectedLabel }}</slot>
            </span>
            <span
                v-if="isSortActive"
                class="catalog-sort__sort-icon sort-swap for-mobile"
            >
                <SortSwapIcon />
            </span>
            <span v-if="!isSortActive" class="catalog-sort__sort-icon">
                <SortArrowIcon width="17" height="18" />
            </span>
            <span v-else class="catalog-sort__icon for-desktop">
                <ArrowIcon
                    height="20"
                    class="catalog-sort__arrow"
                    :class="{ 'icon--rotate-180': orderDir === 'asc' }"
                />
            </span>
        </button>

        <div
            v-if="!buttonOnly && listOpen"
            ref="list"
            class="catalog-sort__list"
            :class="{ 'is-visible': listOpen }"
            tabindex="0"
            role="listbox"
            @keydown="onKeyDown"
        >
            <template
                v-for="({ key, name, direction }, index) in selectOptions"
            >
                <button
                    :key="`${key}-${direction}`"
                    role="option"
                    class="catalog-sort__list-item btn--clear"
                    :class="{
                        'is-active': currentOption === index
                    }"
                    :aria-label="getSortAriaLabel(name, direction)"
                    tabindex="-1"
                    @click="setOrderByList(key, direction)"
                    @keydown.space="setOrderByList(key, direction)"
                    @keydown.enter="setOrderByList(key, direction)"
                >
                    <span class="catalog-sort__list-label">
                        <template v-if="name === 'Price'">Cena</template>
                        <template v-else>{{ name }}</template>
                        <span v-if="!['position', 'created_at'].includes(key)">
                            {{
                                i18n(
                                    direction === 'desc'
                                        ? 'descending'
                                        : 'ascending'
                                )
                            }}
                        </span>
                    </span>
                </button>
            </template>
        </div>
    </div>
</template>
<script>
import { upperFirst } from 'lodash'
import { mapActions, mapState, mapGetters } from 'vuex'
import SortSwapIcon from '@icons/sort-arrows.svg'
import SortArrowIcon from '@icons/sort-swap-arrows.svg'
import SortDotIcon from '@icons/sort-dot.svg'
import ArrowIcon from '@icons/sort_arrow.svg'
import PriceIcon from '@icons/sort_price.svg'
import Created_atIcon from '@icons/ringbell.svg'
import PositionIcon from '@icons/sort_position.svg'
import NameIconA from '@icons/sort_name_a.svg'
import NameIconZ from '@icons/sort_name_z.svg'

export default {
    components: {
        ArrowIcon,
        // eslint-disable-next-line vue/no-unused-components
        PriceIcon,
        // eslint-disable-next-line vue/no-unused-components
        PositionIcon,
        // eslint-disable-next-line vue/no-unused-components
        NameIconA,
        // eslint-disable-next-line vue/no-unused-components
        NameIconZ,
        // eslint-disable-next-line vue/no-unused-components
        Created_atIcon,
        SortSwapIcon,
        SortArrowIcon,
        SortDotIcon
    },
    props: {
        buttonOnly: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            listOpen: false,
            currentOption: -1
        }
    },
    computed: {
        ...mapState({
            orderBy: (state) => state.params.order,
            orderDir: (state) => state.params.dir,
            orderOptions: (state) => state.options.order.options,
            orderDefault: (state) => state.options.order.default
        }),
        ...mapGetters(['i18n']),
        selectedLabel() {
            if (this.currentOption !== -1) {
                const { name, direction } = this.selectOptions[
                    this.currentOption
                ]
                const directionText = ![
                    'position',
                    'created_at',
                    'price'
                ].includes(name)
                    ? this.i18n(
                          direction === 'desc' ? 'descending' : 'ascending'
                      )
                    : ''
                return `${name} ${directionText}`.trim()
            }
            return this.i18n('Sort')
        },
        sortIcon() {
            return this.getIcon(this.orderBy, this.orderDir)
        },
        isSortActive() {
            return (
                this.orderBy !== this.orderDefault.order ||
                (this.orderBy === this.orderDefault.order &&
                    this.orderDir !== this.orderDefault.dir)
            )
        },
        selectedOption() {
            return this.selectOptions[this.currentOption]
        },
        selectOptions() {
            const optionsSegregated = [...Object.entries(this.orderOptions)]

            // change position of created_at option to index no. 1
            const createdAtIndex = optionsSegregated
                .map(([key]) => key)
                .indexOf('created_at')

            if (createdAtIndex !== -1) {
                optionsSegregated.splice(
                    1,
                    0,
                    optionsSegregated.splice(createdAtIndex, 1)[0]
                )
            }

            const selectOptions = []
            for (const [key, name] of optionsSegregated) {
                selectOptions.push({
                    key,
                    name,
                    direction: 'desc'
                })
                if (!['position', 'created_at'].includes(key)) {
                    selectOptions.push({
                        key,
                        name,
                        direction: 'asc'
                    })
                }
            }
            return selectOptions
        }
    },
    beforeMount() {
        this.findCurrentIndex()
    },
    methods: {
        ...mapActions(['setParams', 'getProducts']),
        setOrder(order, dir) {
            this.setParams({ dir, order })
            this.getProducts()
            this.$emit('mobileCloseSort', true)
        },
        getSortAriaLabel(name, direction) {
            const fieldName = name === 'Price' ? 'Cena' : name
            const directionText = !['position', 'created_at'].includes(name)
                ? this.i18n(direction === 'desc' ? 'descending' : 'ascending')
                : ''
            return `Sortuj po ${fieldName} ${directionText}`.trim()
        },
        getIcon(orderBy, dir) {
            let appendix = ''
            if (orderBy === 'name') {
                switch (dir) {
                    case 'asc':
                        appendix = 'A'
                        break
                    case 'desc':
                        appendix = 'Z'
                        break
                }
            }
            return (
                this.$options.components[
                    `${upperFirst(orderBy)}Icon${appendix}`
                ] || null
            )
        },
        toggleList() {
            if (!this.buttonOnly) {
                this.listOpen = !this.listOpen

                this.$nextTick(() => {
                    if (this.listOpen) {
                        this.$refs.list.focus()
                    }
                })
            }
        },
        setOrderByList(dir, order) {
            this.setOrder(dir, order)
            this.toggleList()
            this.findCurrentIndex()
            this.$refs.button.focus()
        },
        onKeyDown(event) {
            if (event.code === 'ArrowDown') {
                event.preventDefault()
                this.currentOption !==
                    Object.keys(this.selectOptions).length - 1 &&
                    this.currentOption++
            }

            if (event.code === 'ArrowUp') {
                event.preventDefault()
                this.currentOption >= 1 && this.currentOption--
            }

            if (event.code === 'Tab') {
                event.preventDefault()
                if (event.shiftKey) {
                    this.currentOption >= 1 && this.currentOption--
                } else {
                    this.currentOption !==
                        Object.keys(this.selectOptions).length - 1 &&
                        this.currentOption++
                }
            }

            if (event.code === 'Space' || event.code === 'Enter') {
                event.preventDefault()
                this.setOrderByList(
                    this.selectedOption.key,
                    this.selectedOption.direction
                )
            }

            if (event.code === 'Escape') {
                this.toggleList()
                this.$refs.button.focus()
            }
        },
        findCurrentIndex() {
            this.currentOption = this.selectOptions.findIndex(
                (item) =>
                    item.direction === this.orderDir &&
                    item.key === this.orderBy
            )
        }
    }
}
</script>
<style scoped lang="scss">
.catalog-sort {
    $b: &;
    position: relative;
    z-index: 6;

    path {
        fill: currentColor;
    }

    &__list {
        position: absolute;
        left: 0;
        right: 0;
        margin-top: -2rem;
        width: 160px;
        background: #fff;
        box-shadow: 0 30px 40px 0 rgba(47, 49, 70, 0.1);
        z-index: 1;
        overflow: hidden;
    }

    &__sort-icon {
        height: 19px;
    }

    button#{&}__list-item {
        position: relative;
        padding: 12px 16px;
        cursor: pointer;
        display: block;
        text-align: initial;
        text-transform: initial;
        width: 100%;

        font-size: 14px;
        font-style: normal;
        font-weight: 600;
        line-height: 22px;

        &:first-of-type {
            padding-top: 3.5rem;
        }

        &:last-of-type {
            border-bottom-left-radius: $button-radius;
            border-bottom-right-radius: $button-radius;
        }

        &.is-active,
        &:hover {
            background-color: $sherpa-blue-light;
        }
    }

    &__btn {
        display: flex;
        align-items: center;
        gap: 4px;
        position: relative;
        z-index: 2;
        background: #fff;
        width: fit-content;
        min-width: 160px;
        height: 40px;
        border-radius: $button-radius;

        svg {
            height: 1.6rem;
            color: $sherpa-blue;
        }

        &:hover {
            svg {
                color: white;
            }
        }

        &.btn--bg {
            background-color: $sherpa-blue;

            svg {
                color: white;
            }
        }

        @media #{$media-to-sm} {
            min-width: 88px;
            width: fit-content;
            font-size: 14px;
        }
    }

    #{$b}__btn {
        @media #{$media-xs} {
            padding: 10px 9px;
        }

        svg {
            @media #{$media-xs} {
                height: 1.6rem;
            }
        }
    }

    &__type {
        margin-left: 0.2rem;

        @media #{$media-xs} {
            margin-left: 0.1rem;
        }
    }

    &__icon {
        display: flex;
    }

    &__list-label {
        span {
            text-transform: lowercase;
        }
    }

    &__list-icon {
        height: 1.7rem;
        position: absolute;

        path {
            fill: $sherpa-blue;
        }
    }
}
.sort-dot {
    margin-top: 14px;
    margin-right: 4px;
}
.sort-swap {
    height: 24px;
    svg {
        overflow: inherit;
    }
}
.for-mobile {
    display: none;

    @media #{$media-to-xs} {
        display: block;
    }
}
.for-desktop {
    display: block;

    @media #{$media-to-xs} {
        display: none;
    }
}
</style>
